import React, { useState } from 'react';

import { faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { Collapse } from 'reactstrap';
import styled, { css, useTheme } from 'styled-components';

import SidebarSubmenuItem from 'components/Layout/Header/components/Sidebar/SidebarSubmenuItem';
import ArrowIcon from 'components/Layout/Header/components/Sidebar/components/ArrowIcon';
import StyledCard from 'components/Layout/Header/components/Sidebar/components/SidebarCard';
import StyledLink from 'components/Layout/Header/components/Sidebar/components/SidebarLink';

import { MenuColumnSubMenuItemI, SubMenuItemI } from 'interfaces/content-stack/contentStack.types';

import { getModularBlockLink } from 'utils/modularBlockUtils';

const MenuTitle = styled.button<{ $isOpen: boolean }>`
  ${({ theme, $isOpen }) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
    font-weight: 600;
    background: none;
    text-align: left;
    font-size: 1rem;
    cursor: pointer;
    padding: 0 1rem;
    border: none;
    height: 3.5rem;
    background-color: ${$isOpen ? theme.colors.whiteSmoke : 'transparent'};
    color: ${$isOpen ? theme.colors.primaryRed : theme.colors.primaryBlack2};
    transition:
      background-color 0.5s cubic-bezier(0.165, 0.84, 0.44, 1),
      color 0.2s ease;
    &:hover {
      background-color: ${theme.colors.whiteSmoke};
      color: ${theme.colors.primaryRed};
    }
  `}
`;

const SidebarItemContainer = styled.div`
  margin-bottom: 0.5rem;
`;

interface SidebarItemProps {
  data: SubMenuItemI;
  toggleSidebar: () => void;
}

const SidebarItem: React.FC<SidebarItemProps> = ({ data, toggleSidebar }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { linkText, link, menuColumns } = data;
  const { submenu } = menuColumns![0];
  const toggle = () => setIsOpen(!isOpen);
  const theme = useTheme();

  return (
    <SidebarItemContainer>
      {submenu?.length > 0 ? (
        <>
          <MenuTitle $isOpen={isOpen} onClick={toggle} data-testid='sidebar-tab-button'>
            {linkText}
            <ArrowIcon $isOpen={isOpen} icon={faChevronRight} />
          </MenuTitle>
          <Collapse isOpen={isOpen}>
            <StyledCard $backgroundColor={theme.colors.whiteSmoke} data-testid='sidebar-tab'>
              {submenu.map((item: MenuColumnSubMenuItemI, index: number) =>
                item.tertiaryMenuItems && item.tertiaryMenuItems.length > 0 ? (
                  <SidebarSubmenuItem
                    key={index}
                    title={item.linkText}
                    submenu={item.tertiaryMenuItems}
                    data-testid='sidebar-item'
                  />
                ) : item.link ? (
                  <StyledLink
                    to={getModularBlockLink(item.link)}
                    key={index}
                    $paddingLeft={'2rem'}
                    $backgroundColor={theme.colors.secondaryGray3}
                    onClick={toggleSidebar}
                    data-testid='sidebar-item'
                  >
                    {item.linkText}
                  </StyledLink>
                ) : (
                  <div key={index}>{item.linkText}</div>
                )
              )}
            </StyledCard>
          </Collapse>
        </>
      ) : link ? (
        <StyledLink
          to={getModularBlockLink(link) ?? ''}
          $paddingLeft={'2rem'}
          $backgroundColor={theme.colors.secondaryGray3}
        >
          {linkText}
        </StyledLink>
      ) : null}
    </SidebarItemContainer>
  );
};

export default SidebarItem;
